// Copied from flowsjs `packages/ui/theme/src/theme.ts` (`@aiuirun/ui-theme`), which is not published.
// Keep in step with the source; console-only overrides live in `consoleTheme.ts`.

import { type Shadows, type Theme, createTheme, useTheme } from "@mui/material/styles";
import { type DesignTokens, darkTokens, lightTokens } from "./tokens";

// ---
// module augmentation — expose the active token set on the theme as `theme.palette.tokens.*`
// ---

declare module "@mui/material/styles" {
  interface Palette {
    tokens: DesignTokens;
  }
  interface PaletteOptions {
    tokens?: DesignTokens;
  }
}

/** The active token set for the current theme (light or dark). Read this in components. */
export function useTokens(): DesignTokens {
  return useTheme().palette.tokens;
}

// ---
// theme factory — one builder, fed either `lightTokens` or `darkTokens`. Every color
// is read from the passed set `t`, so both themes stay in lockstep and only the token values differ.
// ---

function buildTheme(t: DesignTokens, mode: "light" | "dark"): Theme {
  // seed the low elevations with the warm shadows; reuse for higher slots so nothing falls back to
  // MUI's cold-black defaults (MUI needs a 25-slot array; index 0 = 'none')
  const scale = [t.shadow[1], t.shadow[2], t.shadow[3], t.shadow[4]];
  const shadows: string[] = ["none"];
  for (let i = 1; i <= 24; i++) {
    shadows.push(scale[i - 1] ?? (i >= 16 ? t.shadow.overlay : t.shadow[4]));
  }

  return createTheme({
    spacing: 4, // 1 unit = 4px

    palette: {
      mode,
      primary: {
        main: t.accent.base,
        dark: t.accent.hover,
        light: t.accent.soft,
        contrastText: "#FFFFFF",
      },
      secondary: { main: t.ink.secondary, contrastText: "#FFFFFF" },
      success: {
        main: t.semantic.success,
        light: t.semantic.successSoft,
        contrastText: "#FFFFFF",
      },
      warning: {
        main: t.semantic.warning,
        light: t.semantic.warningSoft,
        contrastText: "#FFFFFF",
      },
      error: {
        main: t.semantic.danger,
        light: t.semantic.dangerSoft,
        contrastText: "#FFFFFF",
      },
      info: {
        main: t.semantic.info,
        light: t.semantic.infoSoft,
        contrastText: "#FFFFFF",
      },
      text: {
        primary: t.ink.primary,
        secondary: t.ink.muted,
        disabled: t.ink.faint,
      },
      background: { default: t.surface.bg, paper: t.surface.card },
      divider: t.line.default,
      tokens: t,
    },

    shape: { borderRadius: t.radius.md },

    shadows: shadows as Shadows,

    typography: {
      fontFamily: t.font.sans,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontSize: "2.375rem",
        fontWeight: 500,
        lineHeight: 1.08,
        letterSpacing: "-0.025em",
      },
      h2: {
        fontSize: "1.5rem",
        fontWeight: 500,
        lineHeight: 1.15,
        letterSpacing: "-0.015em",
      },
      h3: {
        fontSize: "1.375rem",
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: "-0.015em",
      },
      h4: {
        fontSize: "1.125rem",
        fontWeight: 500,
        lineHeight: 1.25,
        letterSpacing: "-0.01em",
      },
      body1: {
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: 0,
      },
      body2: {
        fontSize: "0.8125rem",
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: 0,
      },
      subtitle1: {
        fontSize: "0.9688rem",
        fontWeight: 400,
        lineHeight: 1.55,
        letterSpacing: 0,
      },
      button: {
        fontSize: "0.8125rem",
        fontWeight: 500,
        lineHeight: 1,
        textTransform: "none",
      },
      caption: {
        fontSize: "0.75rem",
        fontWeight: 400,
        lineHeight: 1.4,
        letterSpacing: 0,
      },
      overline: {
        fontFamily: t.font.mono,
        fontSize: "0.656rem",
        fontWeight: 500,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      },
    },

    components: {
      // The design's `.btn`: 36 / 28 / 44 tall, and a small button drops to the `md` radius — a
      // control, not a card. Disabled fades the whole button rather than swapping its colours.
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: t.radius.lg,
            paddingInline: 16,
            height: 36,
            "&.Mui-disabled": { opacity: 0.45, color: "inherit", backgroundColor: "inherit" },
          },
          sizeSmall: {
            height: 28,
            paddingInline: 12,
            fontSize: "0.75rem",
            borderRadius: t.radius.md,
          },
          sizeLarge: { height: 44, paddingInline: 22, fontSize: "0.875rem" },
          containedPrimary: {
            boxShadow: t.shadow[1],
            "&:hover": {
              backgroundColor: t.accent.hover,
              boxShadow: t.shadow[1],
            },
            "&.Mui-disabled": { color: "#FFFFFF", backgroundColor: t.accent.base },
          },
          outlined: {
            borderColor: t.line.strong,
            color: t.ink.primary,
            "&:hover": { borderColor: t.line.strong, backgroundColor: t.surface.cardSunken },
          },
          // the design's ghost: quiet ink, an ink-tinted hover — never the accent
          text: {
            color: t.ink.secondary,
            "&:hover": { backgroundColor: t.state.hover },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: t.radius.md,
            backgroundColor: t.surface.card,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: t.line.strong,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: t.line.strong,
            },
            // design focus ring: accent-base @ 22% (0x38 alpha)
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: t.accent.base,
              boxShadow: `0 0 0 3px ${t.accent.base}38`,
            },
          },
          input: {
            padding: "9px 12px",
            fontSize: "13.5px",
            color: t.ink.primary,
          },
        },
      },
      // label sits above the input (not the floating/notched MUI default) — matches the design's .field
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: "12px",
            fontWeight: 500,
            color: t.ink.secondary,
            marginBottom: 7,
            "&.Mui-focused": { color: t.ink.secondary },
            "&.Mui-error": { color: t.semantic.danger },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: "11px",
            color: t.ink.muted,
            marginLeft: 0,
            marginTop: 7,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: t.radius.pill,
            fontWeight: 500,
            fontSize: "0.719rem",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${t.line.default}`,
          },
          rounded: { borderRadius: t.radius.md },
        },
      },
      // `md` is the default shape — cards, inputs — and a card is the thing it was named for. Buttons
      // are the exception at `lg`, not the rule.
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: t.radius.md, boxShadow: t.shadow[2] },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: t.chrome.base,
            color: t.chrome.inkHi,
            borderRadius: t.radius.sm,
            fontSize: "0.75rem",
          },
        },
      },
    },
  });
}

/**
 * A theme from an arbitrary token set.
 *
 * Exported because the colours are baked into `styleOverrides` when the theme is built — a nested
 * `ThemeProvider` that swaps `palette.tokens` repaints nothing, since those overrides captured
 * their values already. Repainting means rebuilding, which is what the signed-out pages do to
 * show a tenant its own accent.
 */
export function themeFrom(t: DesignTokens, mode: "light" | "dark"): Theme {
  return buildTheme(t, mode);
}

/** The MUI theme (light). */
export const lightTheme: Theme = buildTheme(lightTokens, "light");

/** The MUI theme (dark). */
export const darkTheme: Theme = buildTheme(darkTokens, "dark");
